/**
 * 全局应用状态：整个主线程只有这一个 store。
 *
 * - 状态形状在 GlobalState 里集中声明，后续任务（登录态、连接态、会话、
 *   消息……）在此追加字段；业务更新逻辑写成 actions 模块（调 store.set），
 *   不写在组件里。
 * - 数据源接线也集中在 createGlobalStore：layoutEnv（断点/standalone）已挂入，
 *   Worker 推送（连接状态、消息批次）由任务 8/9 接入。
 * - Worker 崩溃重启（worker error → dispose 旧 RpcClient → 重建）也在
 *   这一层接线，建 Worker 入口时一并做（见 docs/worker-arch.md）。
 */
import { createStore, type Store } from './store';
import { AUTH_UNKNOWN, type AuthSlice } from './authState';
import { CONVERSATIONS_INITIAL, type ConversationsSlice } from './conversationsState';
import { MESSAGES_INITIAL, type MessagesSlice } from './messagesState';
import { CONFIG_INITIAL, type ConfigSlice } from './configState';
import { NET_ROUTE_INITIAL, type NetRouteSlice } from './netRouteState';
import { DRAFTS_INITIAL, type DraftsSlice } from './draftsState';
import { ARCHIVE_INITIAL, type ArchiveSlice } from './archiveState';
import { STICKERS_INITIAL, type StickersSlice } from './stickersState';
import { CONTACTS_INITIAL, type ContactsSlice } from './contactsState';
import { PRESENCE_INITIAL, type PresenceSlice } from './presenceState';
import { SW_UPDATE_INITIAL, type SwUpdateSlice } from './swUpdateState';
import { NOTIFY_POPUP_INITIAL, type NotifyPopupSlice } from './notifyPopupState';
import { SERVER_ERROR_NONE, type ServerErrorSlice } from './serverErrorState';
import { createFrameScheduler, type Scheduler } from '~/util/schedule';
import { layoutEnvStore, type LayoutEnv, type LayoutEnvStore } from '~/env/layoutEnv';
import { HOME_CHATS } from '~/nav/routes';
import type { NavState } from '~/nav/router';

export interface GlobalState {
  /** 布局环境（断点/standalone）。数据源是 layoutEnvStore，这里是只读镜像。 */
  layout: LayoutEnv;
  /** 页面栈。数据源是 appRouter（init 时即写入），这里是只读镜像；导航一律走 nav/actions。 */
  nav: NavState;
  /** 鉴权态。数据源是 Worker（store/appWorker 接线），首帧 unknown 等 auth/state 回包。 */
  auth: AuthSlice;
  /** 会话列表。数据源是 Worker（store/conversationsState 接线），null=未加载。 */
  conversations: ConversationsSlice;
  /** 打开中会话的时间线。数据源是 Worker（store/messagesState 接线），由 nav+auth 驱动。 */
  messages: MessagesSlice;
  /** 应用配置（cdn 等）。数据源是 Worker（store/configState 接线），null=未加载。 */
  config: ConfigSlice;
  /** 访问路线（14.39，海外路线开关）。数据源是 Worker 全局库 kv（store/netRouteState 接线），null=未加载。 */
  netRoute: NetRouteSlice;
  /** 草稿镜像（14.12）。数据源是 Worker kv（store/draftsState 接线），null=未加载。 */
  drafts: DraftsSlice;
  /** 归档会话键集（14.36，纯本地）。数据源是 Worker kv（store/archiveState 接线），null=未加载。 */
  archive: ArchiveSlice;
  /** 贴纸面板数据（14.16）。数据源是 Worker（store/stickersState 接线），null=未加载。 */
  stickers: StickersSlice;
  /** 通讯录（14.18）。数据源是 Worker（store/contactsState 接线），字段各自 null=未加载。 */
  contacts: ContactsSlice;
  /** 好友在线状态（14.38）。数据源是 WS presence 推送（store/presenceState 接线），空 map=无已知条目。 */
  presence: PresenceSlice;
  /** SW 版本更新态（15b）。数据源是 ServiceWorkerRegistration 事件（store/swUpdateState 接线）。 */
  swUpdate: SwUpdateSlice;
  /** 窗口内新消息提示条队列（18c2b，仅桌面）。数据源是 store/notifyDelivery 的投递接线。 */
  notifyPopup: NotifyPopupSlice;
  /** 无归属的服务端错误（21f）。数据源是 Worker 'chat/serverError' 推送；seq 变化即一次 toast。 */
  serverError: ServerErrorSlice;
  /**
   * 我的资料版本号（21o）：每次改资料成功 +1。桌面三列下左栏「我的」Tab 不再随
   * 导航重挂，靠它被动重读（store/userProfileState 文件头）。**只是信号不是数据**。
   */
  profileRev: number;
}

export type GlobalStore = Store<GlobalState>;

export interface GlobalStoreDeps {
  layoutEnv: LayoutEnvStore;
  scheduler: Scheduler;
}

/** 工厂（测试注入用）；运行时一律走 globalStore() 单例。 */
export function createGlobalStore(deps: GlobalStoreDeps): GlobalStore {
  const store = createStore<GlobalState>(
    // nav 初值只是渲染兜底：main.tsx 里 appRouter().init() 先于首次渲染执行，会立即覆盖
    {
      layout: deps.layoutEnv.get(),
      nav: { stack: [HOME_CHATS] },
      auth: AUTH_UNKNOWN,
      conversations: CONVERSATIONS_INITIAL,
      messages: MESSAGES_INITIAL,
      config: CONFIG_INITIAL,
      netRoute: NET_ROUTE_INITIAL,
      drafts: DRAFTS_INITIAL,
      archive: ARCHIVE_INITIAL,
      stickers: STICKERS_INITIAL,
      contacts: CONTACTS_INITIAL,
      presence: PRESENCE_INITIAL,
      swUpdate: SW_UPDATE_INITIAL,
      notifyPopup: NOTIFY_POPUP_INITIAL,
      serverError: SERVER_ERROR_NONE,
      profileRev: 0,
    },
    deps.scheduler,
  );
  // layoutEnv 值不变时不通知且引用不变，这里不用再判重
  deps.layoutEnv.subscribe(() => {
    store.set({ layout: deps.layoutEnv.get() });
  });
  return store;
}

let singleton: GlobalStore | undefined;

/** 浏览器环境下的全局单例（懒初始化，避免 node 测试环境 import 即触碰 window）。 */
export function globalStore(): GlobalStore {
  if (!singleton) {
    singleton = createGlobalStore({
      layoutEnv: layoutEnvStore(),
      scheduler: createFrameScheduler(),
    });
  }
  return singleton;
}
